import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  Stack,
  Typography,
  alpha,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CampaignIcon from '@mui/icons-material/Campaign';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PushPinIcon from '@mui/icons-material/PushPin';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import AnnouncementContent, {
  AnnouncementHero, hasWideAttachment, priorityTheme,
} from './AnnouncementContent';
import {
  CATEGORY_OPTIONS, PRIORITY_OPTIONS, findOption, fmtDateTime,
} from '../../views/Humen/announcement/constants';

function ListItem({ a, onClick }) {
  const cat = findOption(CATEGORY_OPTIONS, a.category);
  const pri = findOption(PRIORITY_OPTIONS, a.priority);
  const unread = !a.acknowledged_at;

  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onClick()}
      sx={{
        display: 'flex',
        gap: 1.5,
        p: 1.75,
        borderRadius: 2.5,
        cursor: 'pointer',
        border: '1px solid',
        borderColor: unread ? 'rgb(var(--color-primary-500) / 0.25)' : 'divider',
        borderLeft: `4px solid ${cat.color}`,
        bgcolor: unread ? 'rgb(var(--color-primary-500) / 0.035)' : 'background.paper',
        transition: 'all 0.15s ease',
        outline: 'none',
        '&:hover, &:focus-visible': { boxShadow: '0 4px 14px rgba(17,24,39,0.08)', transform: 'translateY(-1px)' },
      }}
    >
      <Box flexGrow={1} minWidth={0}>
        <Stack direction="row" alignItems="center" spacing={0.75} sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
          <Chip
            size="small"
            label={cat.label}
            sx={{
              height: 22, bgcolor: alpha(cat.color, 0.1), color: cat.color, fontWeight: 600,
            }}
          />
          {a.priority !== 'normal' && <Chip size="small" label={pri.label} color={pri.color} sx={{ height: 22 }} />}
          {a.pinned && <PushPinIcon sx={{ fontSize: 15, color: 'primary.main' }} />}
          {a.attachments?.length > 0 && <AttachFileIcon sx={{ fontSize: 15, color: 'text.disabled' }} />}
        </Stack>
        <Typography sx={{ mt: 0.75, fontWeight: unread ? 700 : 500, fontSize: '0.95rem' }} noWrap>
          {a.title}
        </Typography>
        {a.body && (
          <Typography variant="caption" color="text.secondary" noWrap component="div">
            {a.body.split('\n')[0]}
          </Typography>
        )}
        <Typography variant="caption" color="text.disabled" component="div" sx={{ mt: 0.25 }}>
          {fmtDateTime(a.createdAt)}
        </Typography>
      </Box>
      <Box sx={{ pt: 0.25, flexShrink: 0 }}>
        {unread ? (
          <Chip
            size="small"
            label="ใหม่"
            sx={{
              height: 20, fontSize: '0.7rem', fontWeight: 700, bgcolor: 'error.main', color: '#fff',
            }}
          />
        ) : (
          <CheckCircleIcon sx={{ fontSize: 20, color: 'success.main' }} />
        )}
      </Box>
    </Box>
  );
}

// รายการประกาศทั้งหมดที่ user คนนี้เห็นได้ตอนนี้ — กดเปิดอ่านทีละใบ และกดรับทราบจากในนี้ได้
export default function AnnouncementListDialog({
  open, items, onClose, onOpenItem, onAcknowledge,
}) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [selectedId, setSelectedId] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) setSelectedId(null);
  }, [open]);

  const selected = items.find((a) => String(a.id) === String(selectedId));
  const unreadCount = items.filter((a) => !a.acknowledged_at).length;

  const openItem = (a) => {
    setSelectedId(a.id);
    onOpenItem(a);
  };

  const handleAck = async () => {
    setBusy(true);
    try {
      await onAcknowledge(selected);
    } finally {
      setBusy(false);
    }
  };

  const pt = selected ? priorityTheme(selected.priority) : null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={hasWideAttachment(selected) ? 'md' : 'sm'}
      fullWidth
      fullScreen={fullScreen}
      PaperProps={{ sx: { borderRadius: fullScreen ? 0 : 4, overflow: 'hidden', maxHeight: fullScreen ? '100%' : '88vh' } }}
    >
      {selected ? (
        <>
          <AnnouncementHero
            announcement={selected}
            onClose={onClose}
            onBack={(
              <IconButton
                size="small"
                onClick={() => setSelectedId(null)}
                sx={{ color: '#fff', ml: -1, '&:hover': { bgcolor: 'rgba(255,255,255,0.15)' } }}
              >
                <ArrowBackIcon fontSize="small" />
              </IconButton>
            )}
          />
          <DialogContent sx={{ px: 3, py: 2.5 }}>
            <AnnouncementContent announcement={selected} />
          </DialogContent>
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{
              px: 3, py: 1.75, borderTop: '1px solid', borderColor: 'divider', bgcolor: '#f9fafb',
            }}
          >
            {selected.acknowledged_at ? (
              <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mr: 'auto', color: 'success.main' }}>
                <CheckCircleIcon sx={{ fontSize: 18 }} />
                <Typography variant="body2" fontWeight={600}>
                  {`รับทราบแล้ว · ${fmtDateTime(selected.acknowledged_at)}`}
                </Typography>
              </Stack>
            ) : <Box sx={{ mr: 'auto' }} />}
            <Button color="inherit" onClick={() => setSelectedId(null)}>กลับ</Button>
            {!selected.acknowledged_at && (
              <Button
                variant="contained"
                onClick={handleAck}
                disabled={busy}
                startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <CheckIcon />}
                sx={{ px: 3, background: pt.gradient, boxShadow: `0 4px 12px ${alpha(pt.color, 0.35)}` }}
              >
                รับทราบ
              </Button>
            )}
          </Stack>
        </>
      ) : (
        <>
          <Stack
            direction="row"
            alignItems="center"
            spacing={1.5}
            sx={{ px: 3, py: 2, borderBottom: '1px solid', borderColor: 'divider' }}
          >
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2.5,
                display: 'grid',
                placeItems: 'center',
                color: '#fff',
                background: 'linear-gradient(135deg, rgb(var(--color-primary-450)) 0%, rgb(var(--color-primary-500)) 60%, rgb(var(--color-primary-600)) 100%)',
                boxShadow: '0 4px 12px rgb(var(--color-primary-500) / 0.3)',
                flexShrink: 0,
              }}
            >
              <CampaignIcon fontSize="small" />
            </Box>
            <Box flexGrow={1}>
              <Typography fontWeight={700} fontSize="1.1rem" lineHeight={1.2}>ประกาศ</Typography>
              <Typography variant="caption" color="text.secondary">
                {unreadCount > 0 ? `ยังไม่รับทราบ ${unreadCount} รายการ` : 'อ่านครบทุกประกาศแล้ว'}
              </Typography>
            </Box>
            <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
          </Stack>
          <DialogContent sx={{ px: 2, py: 2, bgcolor: '#f9fafb' }}>
            {items.length === 0 ? (
              <Stack alignItems="center" sx={{ py: 7 }}>
                <Box
                  sx={{
                    width: 72,
                    height: 72,
                    mb: 2,
                    borderRadius: '50%',
                    display: 'grid',
                    placeItems: 'center',
                    bgcolor: 'rgb(var(--color-primary-500) / 0.08)',
                    color: 'primary.main',
                  }}
                >
                  <CampaignIcon sx={{ fontSize: 36 }} />
                </Box>
                <Typography fontWeight={600}>ยังไม่มีประกาศ</Typography>
                <Typography variant="body2" color="text.secondary">ประกาศจากบริษัทจะแสดงที่นี่</Typography>
              </Stack>
            ) : (
              <Stack spacing={1.25}>
                {items.map((a) => <ListItem key={a.id} a={a} onClick={() => openItem(a)} />)}
              </Stack>
            )}
          </DialogContent>
        </>
      )}
    </Dialog>
  );
}
